import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";
import { SUPPORTED, NON_EN } from "../i18n";
import { useLang } from "../lib/i18nContext";
import { EASE } from "../lib/motion";

// Discreet selector: "English | العربية" quick toggle plus a small dropdown
// with every available language. Never visually stronger than the primary CTA.
const LanguageSelector = ({ dark = false, compact = false, dropUp = false }) => {
  const { lang, suggestion, switchLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const current = SUPPORTED.find((l) => l.code === lang);
  const quickAlt =
    lang === "en"
      ? NON_EN.find((l) => l.code === suggestion)
      : SUPPORTED[0];

  const text = dark ? "text-ice hover:text-white" : "text-slate hover:text-ink";
  const active = dark ? "text-white" : "text-ink";

  return (
    <div ref={ref} className="relative flex items-center" data-testid="language-selector">
      {quickAlt && !compact ? (
        <div className="flex items-center gap-1.5 text-xs font-medium" dir="ltr">
          <button
            onClick={() => switchLang(lang)}
            data-testid={`lang-quick-current`}
            className={`transition-colors duration-300 ${active} font-semibold cursor-default`}
          >
            {current.native}
          </button>
          <span className={dark ? "text-ice/60" : "text-slate"} aria-hidden="true">|</span>
          <button
            onClick={() => switchLang(quickAlt.code)}
            data-testid={`lang-quick-${quickAlt.code}`}
            className={`transition-colors duration-300 ${text}`}
          >
            {quickAlt.native}
          </button>
        </div>
      ) : (
        <span className={`flex items-center gap-1.5 text-xs font-medium ${active}`}>
          <Globe size={14} strokeWidth={1.8} />
          {current.native}
        </span>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Language menu"
        aria-expanded={open}
        data-testid="lang-menu-toggle"
        className={`ml-1.5 p-1 transition-transform duration-300 ${text} ${open ? "rotate-180" : ""}`}
      >
        <ChevronDown size={13} strokeWidth={2} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: dropUp ? -6 : 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: dropUp ? -6 : 6 }}
            transition={{ duration: 0.25, ease: EASE }}
            data-testid="lang-dropdown"
            className={`absolute ${dropUp ? "bottom-full mb-3" : "top-full mt-3"} start-0 min-w-[170px] max-h-[45vh] overflow-y-auto border rounded-sm z-50 ${
              dark ? "bg-ink border-white/20" : "bg-white border-ice"
            }`}
          >
            {SUPPORTED.map((l) => (
              <button
                key={l.code}
                onClick={() => {
                  setOpen(false);
                  if (l.code !== lang) switchLang(l.code);
                }}
                data-testid={`lang-option-${l.code}`}
                className={`w-full flex items-center justify-between gap-4 px-4 py-2.5 text-sm transition-colors duration-200 ${
                  dark
                    ? "text-white hover:bg-white/10"
                    : "text-ink hover:bg-ice"
                } ${l.code === lang ? "font-semibold" : ""}`}
              >
                <span>{l.native}</span>
                {l.code === lang && <Check size={14} className="text-pine" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
